<template>
  <div class="row mt-8">
    <div class="mx-auto text-center col-lg-6 col-md-6 col-sm-8 col-10">
      <h2>Knowledge Base</h2>
      <p class="text-lighter">
        Learn how strain, recovery and sleep work hand-in-hand to help users to
        improve their fitness and wellbeing over time.
      </p>
    </div>
  </div>
  <div id="strain" class="row mt-4">
    <div class="mx-auto px-4 col-lg-9 col-md-10 col-sm-10 col-11 help">
      <div class="d-flex align-items-center mb-4">
        <icon-strain class="me-3" />
        <div class="h5 m-0 p-0">Strain</div>
      </div>
      <div id="strain-details" class="accordion">
        <accordion-item
          accordion-id="strain1"
          collapse-id="collapseStrain1"
          active
        >
          <template #question>What is strain?</template>
          <template #answer>
            Strain quantifies your cardiovascular exertion, measuring the physical stress on your body.
            Genodyne tracks your daily activities and workouts on a 0–21 scale and calculates an overall strain score.
          
            <section class="mt-3">
              <p style="font-size: 1rem;font-weight: bold;color: white">Workout Strain</p>
              <p>
                Each workout receives a strain score based on cardiovascular exertion.
                A higher value indicates greater exertion and impact on your body.
              </p>
            </section>

            <section>
              <p style="font-size: 1rem;font-weight: bold;color: white">Day Strain</p>
              <p>
                Your total daily exertion includes recorded workouts and daily activities
                such as walking, chores, and general movement.
              </p>
            </section>
            <div class="callout">
              <div class="icon">💡</div>
              <p>
                <strong>Strain is measured on a logarithmic scale.</strong><br />
                As strain increases, it becomes more challenging to accumulate additional strain.
              </p>
            </div>
          </template>
        </accordion-item>
        <accordion-item accordion-id="strain2" collapse-id="collapseStrain2">
          <template #question>How is strain measured?</template>
          <template #answer>
              <div class="sub">Strain is scored on a scale of 0 to 21.</div>
                <!-- segmented scale bar -->
                <div class="bar" aria-hidden="true">
                  <div class="seg s1"></div>
                  <div class="seg s2"></div>
                  <div class="seg s3"></div>
                  <div class="seg s4"></div>
                </div>

                <!-- scale labels -->
                <div class="scale-labels">
                  <div class="scale-left">
                    <div class="k">Light</div>
                    <div class="v">0–9+</div>
                  </div>
                  <div class="scale-right">
                    <div class="k">Intense</div>
                    <div class="v">18–21</div>
                  </div>
                </div>

                <!-- table rows -->
                <div class="strain-rows">
                  <div class="strain-row">
                    <div class="left">
                      <div class="range">0–9+</div>
                      <div class="tag">Light</div>
                    </div>
                    <div class="right">Encourages recovery</div>
                  </div>

                  <div class="strain-row shaded">
                    <div class="left">
                      <div class="range">10–13+</div>
                      <div class="tag">Moderate</div>
                    </div>
                    <div class="right">Balances fitness and recovery</div>
                  </div>

                  <div class="strain-row">
                    <div class="left">
                      <div class="range">14–17+</div>
                      <div class="tag">High</div>
                    </div>
                    <div class="right">Higher fitness gains but likely detrimental to next day recovery</div>
                  </div>

                  <div class="strain-row shaded">
                    <div class="left">
                      <div class="range">18–21</div>
                      <div class="tag">Intense</div>
                    </div>
                    <div class="right">Higher fitness gains but greater risk of injury</div>
                  </div>
                </div>
          </template>
        </accordion-item>
      </div>
    </div>
  </div>
  <div id="recovery" class="row mt-4">
    <div class="mx-auto px-4 col-lg-9 col-md-10 col-sm-10 col-11 help">
      <div class="d-flex align-items-center mb-4">
        <icon-recovery class="me-3" />
        <div class="h5 m-0 p-0">Recovery</div>
      </div>
      <div id="recovery-details" class="accordion">
        <accordion-item
          accordion-id="recovery1"
          collapse-id="collapseRecovery1"
          active
        >
          <template #question>What is recovery?</template>
          <template #answer>
            Recovery is a measure of how well your body adapts to various
            stressors - physical, mental and emotional. It tells you how
            prepared your body is to perform. After a night's sleep, your
            recovery is calculated as a percentage, and shown as red, orange, or
            green. On red recovery days, rest is likely what your body needs.

            <div class="recovery-callout" role="note" aria-label="Recovery tip">
              <div class="left-rail" aria-hidden="true"></div>

              <div class="content">
                <div class="icon" aria-hidden="true">
                  <icon-sleep />
                </div>

                <p class="text">
                  On <span class="accent">red recovery</span> days, rest is likely what your body needs.
                </p>
              </div>
            </div>
          </template>
        </accordion-item>

        <accordion-item
          accordion-id="recovery2"
          collapse-id="collapseRecovery2"
        >
          <template #question>How is recovery measured?</template>
          <template #answer>
            Recovery is based on heart rate variability (HRV), resting heart
            rate (RHR) and sleep. Scored on a scale of 0-100%, it gives you a
            better understanding of when to take up more strain and when to let
            your body rest.

            <!-- table-->
            <div class="p-3 mt-3 mb-2">
              <div class="d-flex content text-sm text-white p-3 bg-dark">
                <div class="w-15">
                  <span class="square bg-danger me-2"></span>0 - 33%
                </div>
                <div class="text-break">
                  Prioritise rest and relaxing activities
                </div>
              </div>

              <div class="d-flex content text-sm text-white p-3 bg-dark">
                <div class="w-15">
                  <span class="square bg-warning me-2"></span>34 - 66%
                </div>
                <div class="text-break">Ready to take on moderate strain</div>
              </div>

              <div class="d-flex content text-sm text-white p-3 bg-dark">
                <div class="w-15">
                  <span class="square bg-success me-2"></span>67 - 100%
                </div>
                <div class="text-break">
                  Ready to handle vigorous activities
                </div>
              </div>
            </div>
            <div class="recovery-callout" role="note" aria-label="Recovery tip">
              <div class="left-rail" aria-hidden="true"></div>
              <div class="content">
                <div class="icon" aria-hidden="true">
                  <icon-sleep />
                </div>

                <p class="text">
                  Boost your recovery by having a good night's sleep. Keep bed and
                  wake times variability low and balance your strain levels.
                </p>
              </div>
            </div>
          </template>
        </accordion-item>
      </div>
    </div>
  </div>
  <div id="sleep" class="row mt-4">
    <div class="mx-auto px-4 col-lg-9 col-md-10 col-sm-10 col-11 help">
      <div class="d-flex align-items-center mb-4">
        <icon-sleep class="me-3" />
        <div class="h5 m-0 p-0">Sleep</div>
      </div>
      <div id="sleep-details" class="accordion">
        <accordion-item
          accordion-id="sleep1"
          collapse-id="collapseSleep1"
          active
        >
          <template #question>What is sleep quality?</template>
          <template #answer>
            Sleep quality is measured by sleep consistency, amount of
            restorative hours, sleep efficiency, deep sleep continuity, and
            waking events. Your sleep quality is calculated as a percentage and
            displayed as red, orange, or green after a night's sleep.

            <div class="recovery-callout" role="note" aria-label="Recovery tip">
              <div class="left-rail" aria-hidden="true"></div>
              <div class="content">
                <div class="icon" aria-hidden="true">
                  <icon-sleep />
                </div>

                <p class="text">
                  A good night's sleep improves recovery.
                </p>
              </div>
            </div>
          </template>
        </accordion-item>

        <accordion-item accordion-id="sleep2" collapse-id="collapseSleep2" active>
          <template #question>What are the stages of sleep?</template>
          <template #answer>
            When you sleep, your brain goes through natural cycles of activity.
            There are four stages of sleep - awake, light, REM and deep sleep.
            <div>
             <SampleSleepChart> </SampleSleepChart>
            </div>
            <!-- table-->
            <div class="p-3 mt-3 mb-2">
              <div class="d-flex content text-sm text-white p-3 bg-dark">
                <div class="w-15">
                  <span class="square bg-awake me-2"></span>Awake
                </div>
                <div class="text-break">
                  Even in bed, you may have periods of wakefulness.
                </div>
              </div>

              <div class="d-flex content text-sm text-white p-3 bg-dark">
                <div class="w-15">
                  <span class="square bg-sleep-light me-2"></span>Light
                </div>
                <div class="text-break">
                  The physiological stage of sleep that allows you to transition
                  to deep sleep.
                </div>
              </div>

              <div class="d-flex content text-sm text-white p-3 bg-dark">
                <div class="w-15">
                  <span class="square bg-sleep-rem me-2"></span>REM
                </div>
                <div class="text-break">
                  The mentally restorative sleep stage where your body
                  consolidates memory and learning.
                </div>
              </div>

              <div class="d-flex content text-sm text-white p-3 bg-dark">
                <div class="w-15">
                  <span class="square bg-sleep-deep me-2"></span>Deep
                </div>
                <div class="text-break">
                  The physically restorative sleep stage where your body
                  restores muscular, cellular, and skeletal systems.
                </div>
              </div>
            </div>

            <div class="recovery-callout" role="note" aria-label="Recovery tip">
              <div class="left-rail" aria-hidden="true"></div>
              <div class="content">
                <div class="icon" aria-hidden="true">
                  <icon-sleep />
                </div>

                <p class="text">
                  REM and deep sleep makes up the restorative hours.
                </p>
              </div>
            </div>

            <SleepImproveScreen></SleepImproveScreen>
            <SleepDebtInfoScreen></SleepDebtInfoScreen>


          </template>
        </accordion-item>
      </div>
    </div>
  </div>


  <div id="rcmnd-prfls" class="row mt-4">
    <div class="mx-auto px-4 col-lg-9 col-md-10 col-sm-10 col-11 help">
      <div class="d-flex align-items-center mb-4">
        <icon-community class="me-3" />
        <div class="h5 m-0 p-0">Recommended Profiles</div>
      </div>
      <div id="rcmnd-prfls-details" class="accordion">
        <accordion-item
          accordion-id="rcmndPrfls1"
          collapse-id="collapseRcmndPrfls1"
          active
        >
          <template #question>How are profile candidates selected?</template>
          <template #answer>
            The candidate selection criteria leverages on genetic and wellness data to match candidates' traits to job requirements using an A.I. profiler-based technique. Genetic data points include Behavioral Genetics, Congnitive Hereditary, Personality, Diet, Nutrition, Sport Genomics, Mental Health Conditions, Nervous System, Visual System, Skeletal System, Circulation System, Immune System, Respiratory System, Integumentary System and more. Key wellness metrics, including heart rate variability (HRV), resting heart rate (RHR), sleep quality, and overall fitness levels, are also taken into account.
          </template>
        </accordion-item>
      </div>
    </div>
  </div>
</template>

<script>
import IconStrain from "@/shared/Controls/Icons/IconStrain.vue";
import IconRecovery from "@/shared/Controls/Icons/IconRecovery.vue";
import IconSleep from "@/shared/Controls/Icons/IconSleep.vue";
import IconCommunity from "@/shared/Controls/Icons/IconCommunity.vue";

import {useRoute} from "vue-router";
import AccordionItem from "../components/AccordionItem.vue";
import SampleSleepChart from "./SampleSleepChart.vue";
import SleepImproveScreen from "./SleepImproveScreen.vue";
import SleepDebtInfoScreen from "./SleepDebtInfoScreen.vue";
export default {
  name: "KnowledgeBase",
  components: {
    AccordionItem,
    IconStrain,
    IconRecovery,
    IconSleep,
    IconCommunity,
    SampleSleepChart,
    SleepImproveScreen,
    SleepDebtInfoScreen,
  },
  mounted() {
    this.$store.state.layout = "custom";
    const section = useRoute().query.section;
    if(!!section == true) {
      document.getElementById(section).scrollIntoView(true);
    }
  },
};
</script>

<style scoped>
.callout {
  display: flex;
  gap: 12px;
  background: rgba(0, 255, 209, 0.08);
  border: 1px solid rgba(0, 255, 209, 0.2);
  border-radius: 16px;
  padding: 14px;
  margin: 20px 0;
}

.icon {
  font-size: 20px;
}

.strain-measure {
  width: 100%;
  background: #000;
  color: rgba(255, 255, 255, 0.92);
  padding: 22px 18px 18px;
  box-sizing: border-box;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

/* Title */
.h2 {
  margin: 0 0 14px;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.2px;
}

/* Subtitle */
.sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 12px;
}

/* Segmented bar */
.bar {
  height: 22px;
  width: 70%;
  display: grid;
  grid-template-columns: 2.2fr 0.9fr 1fr 1.2fr; /* visually similar to screenshot */
  border-radius: 2px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
}

.seg {
  height: 100%;
}
.s1 {
  background: rgba(57, 54, 54, 0.22);
}
.s2 {
  background: rgba(97, 92, 92, 0.18);
}
.s3 {
  background: rgba(246, 235, 235, 0.16);
}
.s4 {
  background: rgba(231, 226, 226, 0.888);
}

/* Labels under bar */
.scale-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  margin-bottom: 18px;
}

.scale-left .k,
.scale-right .k {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  padding-top: 1.5rem;
}

.scale-left .v,
.scale-right .v {
  margin-top: 2px;
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.45);
  /* padding-top: 1.5rem; */
}

.scale-right {
  text-align: right;
}

/* Rows (table) */
.strain-rows {
  display: grid;
  gap: 10px;
}

.strain-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 16px;
  padding: 16px 14px;
  border-radius: 2px;
  background: transparent;
}

.strain-row.shaded {
  background: rgba(255, 255, 255, 0.12);
}

/* Left column */
.left {
  display: grid;
  gap: 4px;
  align-content: start;
}

.range {
  font-weight: 800;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.tag {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
}

/* Right column */
.right {
  font-size: 13px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.85);
}

.recovery-callout {
  margin-top: 1rem;
  width: 100%;
  background: rgba(24, 26, 28, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  overflow: hidden;
  display: grid;
  grid-template-columns: 0.5rem 1fr;
  box-shadow: 0 0.7rem 2rem rgba(0, 0, 0, 0.35);
}

.left-rail {
  background: linear-gradient(180deg, rgba(0, 255, 209, 0.9), rgba(0, 255, 209, 0.45));
}

.content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.85rem 1rem;
}


.text {
  margin: 0;
  font-size: 1rem;
  letter-spacing: 0.2px;
  color: rgba(160, 255, 230, 0.75);
  line-height: 1.35;
}

.accent {
  color: rgba(0, 255, 209, 0.95);
  font-weight: 800;
}
.sub-title {
  font-size: 1rem;
  font-weight: bold;
  color: white;
}

/* Optional: center on desktop like a mobile screen */
@media (min-width: 768px) {
  .strain-measure {
    max-width: 30rem;
    margin: 0 auto;
  }
}
</style>